// src/components/admin/PendingPaymentsManagement.js
import React, { useState, useEffect } from 'react';
import { useAlert } from '../common/AlertSystem';

const PendingPaymentsManagement = () => {
    const [pendingMembers, setPendingMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState('pending');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(15);
    const [totalItems, setTotalItems] = useState(0);
    const { success, error: showError } = useAlert();

    useEffect(() => {
        fetchPendingMembers();
    }, [filterStatus, currentPage, itemsPerPage]);

    const fetchPendingMembers = async () => {
        try {
            const token = localStorage.getItem('adminToken');

            const statusFilter = filterStatus === 'all' ? '' : `&membership_status=${filterStatus}`;
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/admin/members?page=${currentPage}&limit=${itemsPerPage}&sortBy=created_at&sortOrder=desc${statusFilter}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.ok) {
                const data = await response.json();
                setPendingMembers(data.data.members || []);
                setTotalItems(data.data.pagination?.totalItems || 0);
            } else {
                showError('Failed to fetch pending members');
            }
        } catch (error) {
            console.error('Error fetching pending members:', error);
            showError('Error loading pending members');
        } finally {
            setLoading(false);
        }
    };

    const markAsPaid = async (memberId) => {
        if (!window.confirm('Mark this member as PAID? This will update their payment status.')) {
            return;
        }

        try {
            const token = localStorage.getItem('adminToken');
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/admin/members/${memberId}`,
                {
                    method: 'PATCH',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ payment_status: 'paid' })
                }
            );

            if (response.ok) {
                success('Payment status updated to PAID');
                fetchPendingMembers();
            } else {
                showError('Failed to update payment status');
            }
        } catch (error) {
            console.error('Error updating payment status:', error);
            showError('Error updating payment status');
        }
    };

    const activateMember = async (memberId) => {
        if (!window.confirm('ACTIVATE this member? This will make their membership active.')) {
            return;
        }

        try {
            const token = localStorage.getItem('adminToken');
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/admin/members/${memberId}/status`,
                {
                    method: 'PATCH',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        status: 'active',
                        notes: 'Activated after payment confirmation'
                    })
                }
            );

            if (response.ok) {
                success('Member activated successfully!');
                fetchPendingMembers();
            } else {
                showError('Failed to activate member');
            }
        } catch (error) {
            console.error('Error activating member:', error);
            showError('Error activating member');
        }
    };

    const activateAfterPayment = async (memberId) => {
        if (!window.confirm('CONFIRM PAYMENT & ACTIVATE member? This will:\n1. Mark payment as PAID\n2. Activate membership\n3. Send confirmation email')) {
            return;
        }

        try {
            const token = localStorage.getItem('adminToken');

            // Update both payment_status and membership_status
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/admin/members/${memberId}`,
                {
                    method: 'PATCH',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        payment_status: 'paid',
                        membership_status: 'active'
                    })
                }
            );

            if (response.ok) {
                success('✅ Payment confirmed & member activated!');
                fetchPendingMembers();
            } else {
                showError('Failed to confirm payment and activate member');
            }
        } catch (error) {
            console.error('Error:', error);
            showError('Error processing activation');
        }
    };

    const resendActivation = async (memberId, memberEmail) => {
        if (!window.confirm(`Resend activation link to ${memberEmail}?`)) {
            return;
        }

        try {
            const token = localStorage.getItem('adminToken');
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/members/${memberId}/resend-activation`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            const data = await response.json();

            if (response.ok) {
                success(data.message || 'Activation link sent successfully!');
            } else {
                showError(data.message || 'Failed to resend activation link');
            }
        } catch (error) {
            console.error('Error resending activation:', error);
            showError('Error resending activation link');
        }
    };

    const getMembershipPrice = (type) => {
        const prices = {
            'international': '$20',
            'associate': '$15',
            'professional': '$10',
            'student': '$5',
            'vendor_bronze': '$150',
            'vendor_silver': '$200',
            'vendor_gold': '$250',
            'vendor_platinum': '$300',
            'vendor_diamond': '$500'
        };
        return prices[type?.toLowerCase()] || 'N/A';
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid py-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="h4 mb-1">💳 Pending Payments & Activation</h2>
                    <p className="text-muted mb-0">Track member registrations and payment status</p>
                </div>
                <div>
                    <select
                        className="form-select"
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                    >
                        <option value="pending">Pending Only</option>
                        <option value="all">All Members</option>
                    </select>
                </div>
            </div>

            {/* Statistics Cards */}
            <div className="row mb-4">
                <div className="col-md-3">
                    <div className="card border-0 shadow-sm">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="text-muted mb-1 small">Pending Payment</p>
                                    <h3 className="mb-0">
                                        {pendingMembers.filter(m => m.payment_status === 'pending').length}
                                    </h3>
                                </div>
                                <div className="bg-warning bg-opacity-10 p-3 rounded">
                                    <i className="fas fa-clock text-warning fa-2x"></i>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="card border-0 shadow-sm">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="text-muted mb-1 small">Payment Received</p>
                                    <h3 className="mb-0">
                                        {pendingMembers.filter(m => m.payment_status === 'paid' && m.membership_status === 'pending').length}
                                    </h3>
                                </div>
                                <div className="bg-success bg-opacity-10 p-3 rounded">
                                    <i className="fas fa-check-circle text-success fa-2x"></i>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="card border-0 shadow-sm">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="text-muted mb-1 small">Needs Activation</p>
                                    <h3 className="mb-0">
                                        {pendingMembers.filter(m => m.payment_status === 'paid' && m.membership_status !== 'active').length}
                                    </h3>
                                </div>
                                <div className="bg-info bg-opacity-10 p-3 rounded">
                                    <i className="fas fa-user-check text-info fa-2x"></i>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="card border-0 shadow-sm">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="text-muted mb-1 small">Active Members</p>
                                    <h3 className="mb-0">
                                        {pendingMembers.filter(m => m.membership_status === 'active').length}
                                    </h3>
                                </div>
                                <div className="bg-primary bg-opacity-10 p-3 rounded">
                                    <i className="fas fa-users text-primary fa-2x"></i>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Members Table */}
            <div className="card border-0 shadow-sm">
                <div className="card-body">
                    {/* Pagination Controls - Top */}
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <div>
                            <span className="text-muted">Showing {pendingMembers.length} of {totalItems} members</span>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <label className="text-muted me-2">Show:</label>
                            <select
                                className="form-select form-select-sm"
                                style={{ width: 'auto' }}
                                value={itemsPerPage}
                                onChange={(e) => {
                                    setItemsPerPage(Number(e.target.value));
                                    setCurrentPage(1);
                                }}
                            >
                                <option value="10">10</option>
                                <option value="15">15</option>
                                <option value="20">20</option>
                                <option value="30">30</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                                <option value="500">500</option>
                            </select>
                            <span className="text-muted">per page</span>
                        </div>
                    </div>

                    {pendingMembers.length === 0 ? (
                        <div className="text-center py-5">
                            <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
                            <p className="text-muted">No pending members found</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover">
                                <thead className="table-light">
                                    <tr>
                                        <th>Member #</th>
                                        <th>Name</th>
                                        <th>Email</th>
                                        <th>Membership Type</th>
                                        <th>Expected Fee</th>
                                        <th>Payment Status</th>
                                        <th>Member Status</th>
                                        <th>Registered</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pendingMembers.map((member) => (
                                        <tr key={member.id}>
                                            <td>
                                                <span className="badge bg-secondary">{member.member_number || `#${member.id}`}</span>
                                            </td>
                                            <td>
                                                <strong>{member.first_name} {member.last_name}</strong>
                                            </td>
                                            <td>{member.email}</td>
                                            <td>
                                                <span className="badge bg-info text-capitalize">
                                                    {member.membership_type}
                                                </span>
                                            </td>
                                            <td>
                                                <strong className="text-success">
                                                    {getMembershipPrice(member.membership_type)}
                                                </strong>
                                            </td>
                                            <td>
                                                {member.payment_status === 'paid' ? (
                                                    <span className="badge bg-success">
                                                        <i className="fas fa-check-circle me-1"></i>
                                                        PAID
                                                    </span>
                                                ) : member.payment_status === 'pending' ? (
                                                    <span className="badge bg-warning">
                                                        <i className="fas fa-clock me-1"></i>
                                                        PENDING
                                                    </span>
                                                ) : (
                                                    <span className="badge bg-secondary">{member.payment_status}</span>
                                                )}
                                            </td>
                                            <td>
                                                {member.membership_status === 'active' ? (
                                                    <span className="badge bg-success">ACTIVE</span>
                                                ) : member.membership_status === 'pending' ? (
                                                    <span className="badge bg-warning">PENDING</span>
                                                ) : (
                                                    <span className="badge bg-secondary">{member.membership_status}</span>
                                                )}
                                            </td>
                                            <td className="small text-muted">
                                                {formatDate(member.created_at)}
                                            </td>
                                            <td>
                                                <div className="btn-group btn-group-sm" role="group">
                                                    {member.payment_status === 'pending' && member.membership_status === 'pending' && (
                                                        <button
                                                            className="btn btn-success"
                                                            onClick={() => activateAfterPayment(member.id)}
                                                            title="Confirm payment received and activate member"
                                                        >
                                                            <i className="fas fa-check-double me-1"></i>
                                                            Paid & Activate
                                                        </button>
                                                    )}

                                                    {member.payment_status === 'paid' && member.membership_status === 'pending' && (
                                                        <button
                                                            className="btn btn-primary"
                                                            onClick={() => activateMember(member.id)}
                                                            title="Activate membership"
                                                        >
                                                            <i className="fas fa-user-check me-1"></i>
                                                            Activate Only
                                                        </button>
                                                    )}

                                                    {member.payment_status === 'pending' && (
                                                        <button
                                                            className="btn btn-outline-success"
                                                            onClick={() => markAsPaid(member.id)}
                                                            title="Mark payment as received"
                                                        >
                                                            <i className="fas fa-dollar-sign me-1"></i>
                                                            Mark Paid
                                                        </button>
                                                    )}

                                                    {member.membership_status === 'pending' && (
                                                        <button
                                                            className="btn btn-outline-info"
                                                            onClick={() => resendActivation(member.id, member.email)}
                                                            title="Resend account activation link"
                                                        >
                                                            <i className="fas fa-envelope me-1"></i>
                                                            Resend Link
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Pagination Navigation - Bottom */}
                    {totalItems > itemsPerPage && (
                        <div className="d-flex justify-content-center align-items-center mt-4">
                            <nav>
                                <ul className="pagination mb-0">
                                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                        <button
                                            className="page-link"
                                            onClick={() => setCurrentPage(1)}
                                            disabled={currentPage === 1}
                                        >
                                            First
                                        </button>
                                    </li>
                                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                        <button
                                            className="page-link"
                                            onClick={() => setCurrentPage(currentPage - 1)}
                                            disabled={currentPage === 1}
                                        >
                                            Previous
                                        </button>
                                    </li>
                                    <li className="page-item active">
                                        <span className="page-link">
                                            Page {currentPage} of {Math.ceil(totalItems / itemsPerPage)}
                                        </span>
                                    </li>
                                    <li className={`page-item ${currentPage >= Math.ceil(totalItems / itemsPerPage) ? 'disabled' : ''}`}>
                                        <button
                                            className="page-link"
                                            onClick={() => setCurrentPage(currentPage + 1)}
                                            disabled={currentPage >= Math.ceil(totalItems / itemsPerPage)}
                                        >
                                            Next
                                        </button>
                                    </li>
                                    <li className={`page-item ${currentPage >= Math.ceil(totalItems / itemsPerPage) ? 'disabled' : ''}`}>
                                        <button
                                            className="page-link"
                                            onClick={() => setCurrentPage(Math.ceil(totalItems / itemsPerPage))}
                                            disabled={currentPage >= Math.ceil(totalItems / itemsPerPage)}
                                        >
                                            Last
                                        </button>
                                    </li>
                                </ul>
                            </nav>
                        </div>
                    )}
                </div>
            </div>

            {/* Instructions */}
            <div className="card border-0 bg-light mt-4">
                <div className="card-body">
                    <h6 className="fw-bold mb-3">
                        <i className="fas fa-info-circle text-primary me-2"></i>
                        How to Use This Panel
                    </h6>
                    <div className="row">
                        <div className="col-md-4">
                            <strong>1. Member Registers</strong>
                            <p className="small text-muted mb-0">
                                New registrations appear here with PENDING payment status
                            </p>
                        </div>
                        <div className="col-md-4">
                            <strong>2. Check Payment</strong>
                            <p className="small text-muted mb-0">
                                Verify payment in Stripe/Givebutter dashboard, check email receipt
                            </p>
                        </div>
                        <div className="col-md-4">
                            <strong>3. Activate Member</strong>
                            <p className="small text-muted mb-0">
                                Click "Paid & Activate" to confirm payment and activate membership
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PendingPaymentsManagement;
